import { useState } from 'react'
import { AlertBanner, Box, Button, Group, Segmented, Stack, Text, Title } from '@grovus/design-system'
import { AUTH_COPY, LANG_OPTIONS, MEMBERS_COPY, type Dict, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import type { Theme } from '../lib/preferences'
import { AuthLayout } from '../auth/AuthLayout'
import type { Intent } from '../auth/entry'
import type { Reconciled } from '../auth/reconcile'
import { PermissionScreen } from '../requests/PermissionScreen'
import { InvitationList } from './InvitationList'
import type { Invitations } from './useInvitations'

interface Props {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
  email: string
  /** The session's `canCreateCompanies`: "Create a client", or the "ask" line without a button. */
  canCreate: boolean
  /** The company lost in this session, for the removed statement; `null` after a reload. */
  removed: string | null
  onCreate: () => void
  onSignOut: () => Promise<'ok' | 'error'>
  /** The session's received invitations; the list and the empty statement render only when `ready`. */
  invitations: Invitations
  onReconcile: (intent?: Intent) => Promise<Reconciled>
  /** The boundary's re-entry, for the permission screen (GRO-107). */
  onReenter: () => void
}

/**
 * No client access (GRO-106 design, screen 3): a signed-in user with no membership. It
 * lists the pending invitations, or says there are none, or that access to a company was
 * just lost. A user who may not create clients sees the "ask" line and "Request permission
 * to create clients", which opens the permission screen in place of the page (GRO-107,
 * variant (i)). An Accept here enters the shell on the accepted company.
 */
export function NoAccessPage({ t, lang, setLang, theme, toggleTheme, email, canCreate, removed, onCreate, onSignOut, invitations, onReconcile, onReenter }: Props) {
  const copy = MEMBERS_COPY[lang]
  const { state } = invitations
  const items = state.phase === 'ready' ? state.items : []
  const [signingOut, setSigningOut] = useState<'idle' | 'pending' | 'failed'>('idle')
  const [requesting, setRequesting] = useState(false)
  // On 'ok' the boundary unmounts this page; only a failure is left to show.
  async function signOut() {
    if (signingOut === 'pending') return
    setSigningOut('pending')
    if (await onSignOut() === 'error') setSigningOut('failed')
  }
  return (
    <AuthLayout
      maxWidth={520}
      topRight={(
        <>
          <Segmented
            value={lang}
            onChange={(v) => setLang(v as Lang)}
            data={LANG_OPTIONS}
          />
          <Button variant="default" onClick={toggleTheme}>
            {theme === 'light' ? t.dark : t.light}
          </Button>
        </>
      )}
    >
      {/* An approval the focus recheck reconciled ends the request: the page offers creation again. */}
      {requesting && !canCreate ? (
        <PermissionScreen lang={lang} onReenter={onReenter} onBack={() => setRequesting(false)} />
      ) : (
      <Stack gap={24} data-testid="no-access">
        {signingOut === 'failed' && (
          <AlertBanner tone="bad" action={<Button variant="default" onClick={() => { void signOut() }}>{copy.retry}</Button>}>
            {t.authSignOutFailed}
          </AlertBanner>
        )}
        {removed !== null && <AlertBanner tone="info">{fmt(copy.removed, { name: removed })}</AlertBanner>}
        <Stack gap={8}>
          <Title order={1} fz="xl">{copy.noAccess}</Title>
          {state.phase === 'ready' && <Text className="grv-muted">{items.length > 0 ? copy.ledeInvites : copy.ledeNone}</Text>}
          {state.phase === 'loading' && <Text className="grv-muted" role="status">{copy.invitationsLoading}</Text>}
        </Stack>
        {state.phase === 'failed' && (
          <Group gap="xs" role="alert">
            <Text className="grv-muted">{AUTH_COPY[lang].service_unavailable}</Text>
            <Button size="compact-sm" variant="default" onClick={() => { void invitations.refetch() }}>{copy.retry}</Button>
          </Group>
        )}
        <InvitationList lang={lang} items={items} origin="entry" onReconcile={onReconcile} invitations={invitations} />
        {canCreate ? (
          <Box><Button onClick={onCreate}>{copy.create}</Button></Box>
        ) : (
          <Stack gap={12} align="flex-start">
            <Text className="grv-muted">{copy.ask}</Text>
            <Button variant="default" onClick={() => setRequesting(true)}>{copy.request}</Button>
          </Stack>
        )}
        <Group
          justify="space-between"
          gap="4px 16px"
          pt={16}
          style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
        >
          <Text fz="sm" className="grv-muted" style={{ overflowWrap: 'anywhere' }}>
            {copy.signedIn} <Text span fw={600} fz="sm">{email}</Text>
          </Text>
          <Button variant="subtle" size="xs" loading={signingOut === 'pending'} onClick={() => { void signOut() }}>{copy.signOut}</Button>
        </Group>
      </Stack>
      )}
    </AuthLayout>
  )
}
